import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { WarningAmberRounded, LockRounded } from "@mui/icons-material";
import InputField from "components/Input/InputField";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const formatAud = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : `$${Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

// Builds the "this will be deleted" rows from the preview, omitting empty sections. Prefers the generic
// {label, value} `rows` shape (used by Visa/Insurance/SkillAssessment/RPL's delete-preview) over the legacy
// Admission-specific per-field counts below.
const buildDeleteRows = (p) => {
  if (Array.isArray(p.rows)) {
    return p.rows.map((row) => [row.label, row.value]);
  }
  const rows = [];
  if (p.accountEntriesCount > 0) rows.push(["Account entries", p.accountEntriesCount]);
  if (p.termsCount > 0) rows.push(["Term schedule rows", p.termsCount]);
  if (p.paymentsCount > 0) {
    const parts = [`${p.paymentsCount} payment(s)`];
    if (p.verifiedPaymentsCount > 0) parts.push(`${p.verifiedPaymentsCount} verified`);
    if (p.pendingPaymentsCount > 0) parts.push(`${p.pendingPaymentsCount} pending`);
    rows.push([`Payments (${formatAud(p.paymentsTotalAmount)})`, parts.join(" · ")]);
  }
  if (p.refundsCount > 0) {
    const parts = [`${p.refundsCount} refund(s)`];
    if (p.verifiedRefundsCount > 0) parts.push(`${p.verifiedRefundsCount} verified`);
    rows.push([`Refunds (${formatAud(p.refundsTotalAmount)})`, parts.join(" · ")]);
  }
  if (p.modulePaymentsCount > 0) {
    rows.push([
      `Module payments (${formatAud(p.modulePaymentsTotalAmount)})`,
      `${p.modulePaymentsCount} payment(s)`,
    ]);
  }
  const totalNotes =
    (p.accountNotesCount || 0) +
    (p.admissionDetailsNotesCount || 0) +
    (p.termPaymentNotesCount || 0);
  if (totalNotes > 0) rows.push(["Notes", totalNotes]);
  if (p.providerAdmissionsCount > 0) rows.push(["Provider admissions", p.providerAdmissionsCount]);
  if (p.invoiceItemsUnlinkedCount > 0)
    rows.push(["Invoice items (unlinked, kept)", p.invoiceItemsUnlinkedCount]);
  if (p.verificationsCount > 0) {
    const parts = [`${p.verificationsCount} verification(s)`];
    if (p.pendingVerificationsCount > 0) parts.push(`${p.pendingVerificationsCount} pending`);
    rows.push(["Account verifications", parts.join(" · ")]);
  }
  if (p.admissionStepsCount > 0) rows.push(["Admission steps", p.admissionStepsCount]);
  if (p.admissionDocumentsCount > 0) rows.push(["Admission documents", p.admissionDocumentsCount]);
  if (p.changeLogsCount > 0) rows.push(["Change log entries", p.changeLogsCount]);
  if (p.commissionsCount > 0) rows.push(["Commission records", p.commissionsCount]);
  if (p.workInfoCount > 0) rows.push(["Work info records", p.workInfoCount]);
  if (p.accountGroupMembershipsCount > 0) rows.push(["Account group membership", "removed"]);
  return rows;
};

export default function DeleteModal({
  open,
  item,
  handleCancel,
  handleDelete,
  warningItems,
  preview,
  previewLoading = false,
  extraContent,
  bodyText,
}) {
  const [text, setText] = React.useState("");
  const blocked = !!preview && preview.canDelete === false;
  const deleteDisabled = text !== "DELETE" || blocked || previewLoading;

  React.useEffect(() => {
    if (!open) setText("");
  }, [open]);

  return (
    <div>
      <Dialog open={open} TransitionComponent={Transition} keepMounted onClose={handleCancel}>
        <DialogTitle className="font-bold uppercase">Delete {item || "item"} ?</DialogTitle>
        <hr />
        <DialogContent>
          <DialogContentText className="text-lg py-3">
            {bodyText ||
              `Are you sure want to delete the selected ${
                item || "item"
              }. Once you delete, it can't be retrieved back.`}
          </DialogContentText>
          {previewLoading && !preview && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-app-text-secondary">
              Loading what will be deleted…
            </div>
          )}
          {preview && (
            <div className="mb-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="text-sm font-semibold text-app-text-primary">
                  {preview.title || preview.studentName || "Admission"}
                </div>
                {(preview.subtitle || preview.course || preview.university) && (
                  <div className="text-xs text-app-text-secondary">
                    {preview.subtitle ||
                      [preview.course, preview.university].filter(Boolean).join(" · ")}
                    {preview.intake ? ` · Intake ${preview.intake}` : ""}
                  </div>
                )}
              </div>
            </div>
          )}
          {blocked && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4">
              <div className="flex items-center gap-2 mb-1">
                <LockRounded className="text-red-600" fontSize="small" />
                <span className="font-semibold text-red-700 text-sm">Super admin required</span>
              </div>
              <p className="text-red-700 text-sm">{preview?.deleteBlockedReason}</p>
            </div>
          )}
          {preview?.warnings?.length > 0 && (
            <div className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <WarningAmberRounded className="text-red-600" fontSize="small" />
                <span className="font-semibold text-red-700 text-sm">This action will:</span>
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {preview.warnings.map((w, i) => (
                  <li key={i} className="text-red-700 text-sm">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {!preview && warningItems?.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <WarningAmberRounded className="text-amber-500" fontSize="small" />
                <span className="font-semibold text-amber-800 text-sm">This action will also:</span>
              </div>
              <ul className="list-disc pl-5 space-y-1">
                {warningItems.map((w, i) => (
                  <li key={i} className="text-amber-800 text-sm">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {preview && buildDeleteRows(preview).length > 0 && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-app-text-secondary mb-2">
                Will be permanently deleted
              </div>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm">
                {buildDeleteRows(preview).map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4">
                    <dt className="text-app-text-secondary">{label}</dt>
                    <dd className="text-right font-medium text-app-text-primary">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {extraContent}
          Please type DELETE to confirm.
          <InputField
            type="text"
            placeholder="Enter DELETE"
            name="delete"
            required
            outerClassName="mt-3"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </DialogContent>
        <hr />
        <DialogActions className="py-2">
          <Button onClick={handleCancel} variant="outlined" color="error">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteDisabled}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
